import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosInstance from "../../../Config/axiosInstance";
import "./AddExternalResourceInvoice.css";
import { createPortal } from "react-dom";
import { Typography } from '@mui/material';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { NumericFormat } from "react-number-format";

const formatDateLabel = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(year, month - 1, day);

  return new Intl.DateTimeFormat("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
};

const AddExternalResourceInvoice = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    project_code: "",
    cost_id: "",
    supplier: "",
    invoice_number: "",
    invoice_date: "",
    bill_maturity: "",
    invoice_date_display: "",
    bill_maturity_display: "",
    invoice_without_vat: "",
    supplier_vat: "21",
    total_invoice_with_vat: "",
    currency: "",
    exchange_rate: "",
    invoice_without_vat_system: "",
    total_invoice_with_vat_system: "",
    medium_alert: "5",
    critical_alert: "1",
    remarks: "",
    month: "",
    year: "",
    payments_status: "Unpaid"
  });

  const animatedComponents = makeAnimated();

  // All suppliers (we'll filter this based on cost)
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [errors, setErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const [currencies, setCurrencies] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedExchangeRate, setSelectedExchangeRate] = useState(null);

  const exchangeOptionsByCurrencyId = useMemo(() => {
    const map = new Map();

    for (const rate of exchangeRates) {
      const currencyId = Number(rate.currency);
      if (!map.has(currencyId)) map.set(currencyId, []);

      map.get(currencyId).push({
        value: rate.id,
        label: String(rate.value ?? ""),
        dateLabel: rate.date || "",
        rateValue: Number(rate.value),
        rateDate: rate.date,
      });
    }

    for (const [currencyId, options] of map.entries()) {
      options.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      map.set(currencyId, options);
    }

    return map;
  }, [exchangeRates]);

  const isSystemCurrency = useMemo(() => {
    if (!formData.currency || !systemCurrencyId) return false;
    return Number(formData.currency) === Number(systemCurrencyId);
  }, [formData.currency, systemCurrencyId]);

  const exchangeOptions = useMemo(() => {
    if (!formData.currency) return [];
    return exchangeOptionsByCurrencyId.get(Number(formData.currency)) || [];
  }, [formData.currency, exchangeOptionsByCurrencyId]);

  const calculateSystemAmounts = useCallback(
    (invoiceWithoutVat, totalWithVat, currencyId, exchangeRateOption) => {
      const withoutVat = Number(invoiceWithoutVat);
      const withVat = Number(totalWithVat);

      if (!currencyId || !systemCurrencyId) {
        return {
          invoice_without_vat_system: "",
          total_invoice_with_vat_system: "",
        };
      }

      if (Number(currencyId) === Number(systemCurrencyId)) {
        return {
          invoice_without_vat_system: Number.isFinite(withoutVat)
            ? withoutVat.toFixed(2)
            : "",
          total_invoice_with_vat_system: Number.isFinite(withVat)
            ? withVat.toFixed(2)
            : "",
        };
      }

      const rateValue = Number(exchangeRateOption?.rateValue);

      if (!Number.isFinite(rateValue) || rateValue <= 0) {
        return {
          invoice_without_vat_system: "",
          total_invoice_with_vat_system: "",
        };
      }

      return {
        invoice_without_vat_system: Number.isFinite(withoutVat)
          ? (withoutVat * rateValue).toFixed(2)
          : "",
        total_invoice_with_vat_system: Number.isFinite(withVat)
          ? (withVat * rateValue).toFixed(2)
          : "",
      };
    },
    [systemCurrencyId]
  );

  // State for notifications
  const [showToast, setShowToast] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSupplierChange = (selectedOption) => {
    setFormData({ ...formData, supplier: selectedOption ? selectedOption.value : "" });
    if (fieldErrors.supplier) {
      setFieldErrors(prev => ({ ...prev, supplier: "" }));
    }
  };

  const handleCurrencyChange = (selectedOption) => {
    setSelectedCurrency(selectedOption);

    if (!selectedOption) {
      setSelectedExchangeRate(null);

      setFormData((prev) => ({
        ...prev,
        currency: "",
        exchange_rate: "",
        invoice_without_vat_system: "",
        total_invoice_with_vat_system: "",
      }));

      setFieldErrors((prev) => ({
        ...prev,
        currency: "",
        exchange_rate: "",
      }));

      return;
    }

    if (
      systemCurrencyId &&
      Number(selectedOption.value) === Number(systemCurrencyId)
    ) {
      const systemAmounts = calculateSystemAmounts(
        formData.invoice_without_vat,
        formData.total_invoice_with_vat,
        selectedOption.value,
        null
      );

      setSelectedExchangeRate(null);

      setFormData((prev) => ({
        ...prev,
        currency: selectedOption.value,
        exchange_rate: "",
        ...systemAmounts,
      }));
    } else {
      const optionsForCurrency =
        exchangeOptionsByCurrencyId.get(Number(selectedOption.value)) || [];

      const latestRate = optionsForCurrency.length ? optionsForCurrency[0] : null;

      const systemAmounts = calculateSystemAmounts(
        formData.invoice_without_vat,
        formData.total_invoice_with_vat,
        selectedOption.value,
        latestRate
      );

      setSelectedExchangeRate(latestRate);

      setFormData((prev) => ({
        ...prev,
        currency: selectedOption.value,
        exchange_rate: latestRate ? latestRate.value : "",
        ...systemAmounts,
      }));
    }

    setFieldErrors((prev) => ({
      ...prev,
      currency: "",
      exchange_rate: "",
    }));
  };

  const handleExchangeRateChange = (selectedOption) => {
    setSelectedExchangeRate(selectedOption);

    const systemAmounts = calculateSystemAmounts(
      formData.invoice_without_vat,
      formData.total_invoice_with_vat,
      formData.currency,
      selectedOption
    );

    setFormData((prev) => ({
      ...prev,
      exchange_rate: selectedOption ? selectedOption.value : "",
      ...systemAmounts,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      exchange_rate: "",
    }));
  };

  // Fetch all suppliers initially
  useEffect(() => {
    axiosInstance
      .get("/suppliers/")
      .then((response) => {
        const supplierOptions = response.data.map((supplier) => ({
          value: supplier.id,
          label: supplier.name,
        }));
        setAllSuppliers(supplierOptions);
      })
      .catch((error) => console.error("Error preluare furnizori:", error));
  }, []);

  useEffect(() => {
    const fetchCurrencyData = async () => {
      try {
        const [currenciesRes, exchangeRes, systemRes] = await Promise.all([
          axiosInstance.get("/currencies/"),
          axiosInstance.get("/exchange-rate/"),
          axiosInstance.get("/system-currency/"),
        ]);

        const formattedCurrencies = (currenciesRes.data || []).map((curr) => ({
          value: curr.id,
          label: curr.abbreviation || curr.name,
          abbreviation: curr.abbreviation,
          name: curr.name,
        }));

        setCurrencies(formattedCurrencies);

        setExchangeRates(
          (exchangeRes.data || []).sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          )
        );

        if (Array.isArray(systemRes.data) && systemRes.data.length > 0) {
          const currencyId = systemRes.data[0]?.currency ?? null;
          setSystemCurrencyId(currencyId || null);

          const sys = formattedCurrencies.find(
            (c) => Number(c.value) === Number(currencyId)
          );

          setSystemCurrencyLabel(sys?.abbreviation || sys?.name || "");
        }
      } catch (error) {
        console.error("Error loading currency data:", error);
      }
    };

    fetchCurrencyData();
  }, []);

  // Project Codes
  const [projectCodes, setProjectCodes] = useState([]);
  useEffect(() => {
    axiosInstance
      .get('/project-codes/')
      .then(response => {
        const formattedProjectCodes = response.data.map(pc => ({
          value: pc.id,
          label: `${pc.code} - ${pc.description}`
        }));
        setProjectCodes(formattedProjectCodes);
      })
      .catch(error => console.error("Eroare la preluarea codurilor de proiect:", error));
  }, []);

  const [selectedProjectCode, setSelectedProjectCode] = useState(null);
  const handleProjectCodeChange = (selectedOption) => {
    setSelectedProjectCode(selectedOption);
    setSelectedCost(null);
    setFormData((prevFormData) => ({
      ...prevFormData,
      project_code: selectedOption ? selectedOption.value : null,
      cost_id: "",
      supplier: ""
    }));
    setFilteredCost([]);
    setFilteredSuppliers([]);

    if (fieldErrors.project_code) {
      setFieldErrors(prev => ({ ...prev, project_code: "" }));
    }
  };

  // Cost - Fetch all with source_type = External
  const [CostID, setCost] = useState([]);
  const [selectedCost, setSelectedCost] = useState(null);

  useEffect(() => {
    const fetchCosts = async () => {
      try {
        const [projectBudgetRes, budgetStructureRes] = await Promise.all([
          axiosInstance.get('/project-budget/'),
          axiosInstance.get('/budget-structure/')
        ]);

        const externalCostElement = projectBudgetRes.data.filter(
          (pb) => pb.source_type === "External"
        );

        const budgetStructures = budgetStructureRes.data;

        const updatedCosts = externalCostElement.map((dl) => {
          const matchedStructure = budgetStructures.find(
            (bs) => bs.id === dl.cost_element
          );

          const costElementDetails = {
            number: matchedStructure?.cost_element_id || dl.cost_element_id || "N/A",
            description: matchedStructure?.cost_element_description || dl.cost_element_description || "No description",
            projectCode: matchedStructure?.project_code_details?.project_code || dl.project_code,
            projectDetails: matchedStructure?.project_code_details || null,
            offerDetails: matchedStructure?.offer_code_details || null,
            externalSupplier: dl.supplier
          };

          return {
            value: dl.id,
            label: `${costElementDetails.number} - ${costElementDetails.description}`,
            projectCode: costElementDetails.projectCode,
            projectDetails: costElementDetails.projectDetails,
            offerDetails: costElementDetails.offerDetails,
            externalSupplier: costElementDetails.externalSupplier,
            originalData: {
              cost: dl,
              matchedStructure: matchedStructure
            }
          };
        });

        setCost(updatedCosts);
      } catch (error) {
        console.error("Error fetching costs:", error);
      }
    };

    fetchCosts();
  }, []);

  const [filteredCosts, setFilteredCost] = useState([]);

  useEffect(() => {
    if (formData.project_code && CostID.length > 0) {
      const selectedProject = projectCodes.find(pc => pc.value === formData.project_code);
      const selectedProjectCode = selectedProject?.label.split(' - ')[0];

      const filtered = CostID.filter(cost =>
        cost.projectCode === selectedProjectCode
      );

      setFilteredCost(filtered);
    } else {
      setFilteredCost([]);
    }
  }, [formData.project_code, CostID, projectCodes]);

  // Sincronizare selectedCost cu formData.cost_id
  useEffect(() => {
    if (formData.cost_id) {
      const cost = filteredCosts.find(
        d => d.value === formData.cost_id
      );
      setSelectedCost(cost || null);
    } else {
      setSelectedCost(null);
    }
  }, [formData.cost_id, filteredCosts]);

  // Filter suppliers when cost is selected
  const handleCostChange = (selectedOption) => {
    setSelectedCost(selectedOption);
    setFormData(prev => ({
      ...prev,
      cost_id: selectedOption ? selectedOption.value : "",
      supplier: ""
    }));

    if (selectedOption && selectedOption.externalSupplier) {
      const matchingSupplier = allSuppliers.find(
        s => s.value === selectedOption.externalSupplier
      );
      setFilteredSuppliers(matchingSupplier ? [matchingSupplier] : []);
    } else {
      setFilteredSuppliers([]);
    }

    if (fieldErrors.cost_id) {
      setFieldErrors(prev => ({ ...prev, cost_id: "" }));
    }
  };

  // Month
  const [monthChoices, setMonthChoices] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/month-choices/")
      .then((response) => {
        if (response.data.month_choices) {
          const options = response.data.month_choices.map(([value, label]) => ({
            value,
            label
          }));
          setMonthChoices(options);
        } else {
          console.error("month_choices not found in the API response");
        }
      })
      .catch((error) => console.error("Error fetching month choices:", error));
  }, []);

  // Set year and month automatically from invoice date
  useEffect(() => {
    if (formData.invoice_date) {
      const date = new Date(formData.invoice_date);

      // Set year
      const year = date.getFullYear().toString();
      setFormData(prevData => ({
        ...prevData,
        year: year
      }));

      // Set month - convert from 0-11 to "01"-"12"
      const monthIndex = date.getMonth();
      const monthValue = String(monthIndex + 1).padStart(2, '0');

      setFormData(prevData => ({
        ...prevData,
        month: monthValue
      }));

      // Șterge erorile dacă există
      setFieldErrors(prev => ({
        ...prev,
        month: "",
        year: ""
      }));
    }
  }, [formData.invoice_date]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }

    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Date picker handler
  const handleDatePick = (field, date) => {
    if (!date) {
      setFormData(prev => ({
        ...prev,
        [field]: "",
        [field + "_display"]: "",
      }));
      return;
    }

    const iso = format(date, "yyyy-MM-dd");
    const display = format(date, "dd.MM.yyyy");

    setFormData(prev => ({
      ...prev,
      [field]: iso,
      [field + "_display"]: display,
    }));

    // Clear error when date is selected
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Form validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.project_code) newErrors.project_code = "Required";
    if (!formData.cost_id) newErrors.cost_id = "Required";
    if (!formData.supplier) newErrors.supplier = "Required";
    if (!formData.invoice_number) newErrors.invoice_number = "Required";
    if (!formData.invoice_date) newErrors.invoice_date = "Required";
    if (!formData.bill_maturity) newErrors.bill_maturity = "Required";
    if (!formData.invoice_without_vat) newErrors.invoice_without_vat = "Required";
    if (!formData.supplier_vat) newErrors.supplier_vat = "Required";
    if (!formData.currency) newErrors.currency = "Required";

    if (!isSystemCurrency && !formData.exchange_rate) {
      newErrors.exchange_rate = "Exchange rate required";
    }

    // Additional validations
    if (!formData.total_invoice_with_vat || formData.total_invoice_with_vat.trim() === "") {
      newErrors.total_invoice_with_vat = "Required";
    }
    if (!formData.month) newErrors.month = "Required";
    if (!formData.year) newErrors.year = "Required";


    if (!formData.medium_alert || formData.medium_alert === '') {
      newErrors.medium_alert = "Required";
    }
    if (!formData.critical_alert || formData.critical_alert === '') {
      newErrors.critical_alert = "Required";
    }
    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      await axiosInstance.post("/external-resource-invoice/", formData);

      // Show success toast
      setShowToast(true);

      // Navigate back after showing toast
      setTimeout(() => {
        setShowToast(false);
        navigate(-1);
      }, 1500);

    } catch (error) {
      console.error("Error:", error.response?.data || error.message);

      if (error.response?.data) {
        const backendErrors = error.response.data;
        const newErrors = {};

        if (backendErrors.invoice_number) {
          newErrors.invoice_number = ["This number already exists."];
        }

        if (backendErrors.currency) {
          newErrors.currency = Array.isArray(backendErrors.currency)
            ? backendErrors.currency[0]
            : backendErrors.currency;
        }

        if (backendErrors.exchange_rate) {
          newErrors.exchange_rate = Array.isArray(backendErrors.exchange_rate)
            ? backendErrors.exchange_rate[0]
            : backendErrors.exchange_rate;
        }

        setErrors(newErrors);
        setFieldErrors((prev) => ({ ...prev, ...newErrors }));
      } else {
        setErrors({ general: "A apărut o eroare neașteptată." });
      }

      setIsSaving(false);
    }
  };

  // Calculate total invoice with VAT
  useEffect(() => {
    if (formData.invoice_without_vat && formData.supplier_vat) {
      const invoiceWithoutVat = parseFloat(formData.invoice_without_vat);
      const vatPercentage = parseFloat(formData.supplier_vat);

      if (!isNaN(invoiceWithoutVat) && !isNaN(vatPercentage)) {
        const totalWithVat = invoiceWithoutVat * (1 + vatPercentage / 100);

        const systemAmounts = calculateSystemAmounts(
          invoiceWithoutVat,
          totalWithVat,
          formData.currency,
          selectedExchangeRate
        );

        setFormData((prevData) => ({
          ...prevData,
          total_invoice_with_vat: totalWithVat.toFixed(2),
          ...systemAmounts,
        }));

        // Șterge eroarea dacă există
        if (fieldErrors.total_invoice_with_vat) {
          setFieldErrors(prev => ({ ...prev, total_invoice_with_vat: "" }));
        }
      }
    } else {
      // Dacă unul dintre câmpuri este gol, setează totalul la ""
      setFormData((prevData) => ({
        ...prevData,
        total_invoice_with_vat: "",
        invoice_without_vat_system: "",
        total_invoice_with_vat_system: "",
      }));
    }
  }, [
    formData.invoice_without_vat,
    formData.supplier_vat,
    formData.currency,
    selectedExchangeRate,
    calculateSystemAmounts,
    fieldErrors.total_invoice_with_vat
  ]);

  // Error styling
  const errorStyle = {
    color: "#d32f2f",
    fontSize: "11px",
    marginTop: "-4px",
    marginBottom: "0px",
    lineHeight: "1",
  };

  // Error message component for individual fields
  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
      <div style={errorStyle}>
        {error}
      </div>
    );
  };

  // Error message component for row fields
  const RowErrorMessage = ({ errors }) => {
    const hasErrors = Object.values(errors).some(error => error);
    if (!hasErrors) return null;

    return (
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: "2px",
        height: "16px"
      }}>
        {Object.entries(errors).map(([field, error]) => (
          <div key={field} style={{ width: "48%" }}>
            {error && <div style={errorStyle}>{error}</div>}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {showToast &&
        createPortal(
          <div className="global-toast-on-top">
            ✔ External Resource Invoice added successfully!
          </div>,
          document.body
        )
      }

      <div className="add-ext-resource-invoice-page">
        <div className="add-container">
          <div className="header">
            <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
            <h2 className="add-title">Add External Resource Invoice</h2>
          </div>
        </div>
        <hr className="title-separator" />
        <div className="add-container">
          <form onSubmit={handleSubmit} className="add-form">
            {/* Project Code */}
            <div className="form-group">
              <label htmlFor="project_code" className="label">
                Project Code <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="project_code"
                name="project_code"
                options={projectCodes}
                classNamePrefix="react-select"
                value={selectedProjectCode}
                onChange={handleProjectCodeChange}
                placeholder="Select project code"
                components={animatedComponents}
                isClearable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    borderRadius: "8px",
                    borderColor: fieldErrors.project_code ? "#d32f2f" : provided.borderColor,
                    "&:hover": {
                      borderColor: fieldErrors.project_code ? "#d32f2f" : provided.borderColor,
                    },
                  }),
                }}
              />
              <ErrorMessage error={fieldErrors.project_code} />
            </div>

            {/* Cot ID */}
            <div className="form-group">
              <label htmlFor="cost_id" className="label">
                Cost ID <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="cost_id"
                name="cost_id"
                classNamePrefix="react-select"
                options={filteredCosts}
                value={selectedCost}
                onChange={handleCostChange}
                placeholder={filteredCosts.length ? "Select cost" : "First select a project"}
                components={animatedComponents}
                isClearable
                isDisabled={!formData.project_code}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    borderRadius: "8px",
                    borderColor: fieldErrors.cost_id ? "#d32f2f" : provided.borderColor,
                    "&:hover": {
                      borderColor: fieldErrors.cost_id ? "#d32f2f" : provided.borderColor,
                    },
                  }),
                }}
              />
              <ErrorMessage error={fieldErrors.cost_id} />
            </div>

            {/* Supplier */}
            <div className="form-group">
              <label htmlFor="supplier" className="label">
                Supplier name <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="supplier"
                name="supplier"
                classNamePrefix="react-select"
                options={filteredSuppliers}
                value={filteredSuppliers.find((s) => s.value === formData.supplier) || null}
                onChange={handleSupplierChange}
                placeholder={filteredSuppliers.length ? "Select supplier" : "First select a cost element"}
                components={animatedComponents}
                isClearable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    borderRadius: "8px",
                    borderColor: fieldErrors.supplier ? "#d32f2f" : provided.borderColor,
                    "&:hover": {
                      borderColor: fieldErrors.supplier ? "#d32f2f" : provided.borderColor,
                    },
                  }),
                }}
                isDisabled={!formData.cost_id || filteredSuppliers.length === 0}
              />
              <ErrorMessage error={fieldErrors.supplier} />
              {formData.cost_id && filteredSuppliers.length === 0 && (
                <div style={{ color: 'red', fontSize: '12px', marginTop: '2px' }}>
                  No supplier found for the selected cost.
                </div>
              )}
            </div>

            {/* Invoice Number and Date - ROW 1 */}
            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="invoice_number" className="label">
                  Invoice number <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="invoice_number"
                  name="invoice_number"
                  value={formData.invoice_number}
                  onChange={handleChange}
                  className={`input ${errors.invoice_number || fieldErrors.invoice_number ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.invoice_number ? "#d32f2f" : "" }}
                  autoComplete="off"
                />
              </div>

              <div className="form-group">
                <label className="label">
                  Invoice Date <span style={{ color: 'red' }}>*</span>
                </label>

                <div className="datepicker-container">
                  {/* INPUT NORMAL – ARATĂ CA TOATE CELELALTE */}
                  <input
                    type="text"
                    value={formData.invoice_date_display}
                    placeholder="dd.mm.yyyy"
                    className={`input ${fieldErrors.invoice_date ? "input-error" : ""}`}
                    readOnly
                    onClick={() => document.querySelector("#invoice_date_picker")?.focus()}
                  />

                  {/* DATEPICKER – ASCUNS */}
                  <DatePicker
                    id="invoice_date_picker"
                    selected={formData.invoice_date ? new Date(formData.invoice_date) : null}
                    onChange={(date) => handleDatePick("invoice_date", date)}
                    dateFormat="dd.MM.yyyy"
                    className="datepicker-hidden"
                    autoComplete="off"
                  />

                  {/* ICONIȚA */}
                  <span
                    className="calendar-icon-inside"
                    onClick={() => document.querySelector("#invoice_date_picker")?.focus()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <RowErrorMessage errors={{
              invoice_number: fieldErrors.invoice_number,
              invoice_date: fieldErrors.invoice_date
            }} />

            {errors.invoice_number && (
              <div className="error-message-global">
                {errors.invoice_number[0]}
              </div>
            )}

            {/* Bill Maturity and Invoice Without VAT - ROW 2 */}
            <div className="form-group-row">
              <div className="form-group">
                <label className="label">
                  Bill Maturity <span style={{ color: 'red' }}>*</span>
                </label>

                <div className="datepicker-container">
                  {/* Input vizibil */}
                  <input
                    type="text"
                    value={formData.bill_maturity_display}
                    placeholder="dd.mm.yyyy"
                    className={`input ${fieldErrors.bill_maturity ? "input-error" : ""}`}
                    readOnly
                    onClick={() => document.querySelector("#bill_maturity_picker")?.focus()}
                  />

                  {/* DATEPICKER CORECT */}
                  <DatePicker
                    id="bill_maturity_picker"
                    selected={formData.bill_maturity ? new Date(formData.bill_maturity) : null}
                    onChange={(date) => handleDatePick("bill_maturity", date)}
                    dateFormat="dd.MM.yyyy"
                    className="datepicker-hidden"
                    autoComplete="off"
                  />

                  {/* Iconiță calendar */}
                  <span
                    className="calendar-icon-inside"
                    onClick={() => document.querySelector("#bill_maturity_picker")?.focus()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </span>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="invoice_without_vat" className="label">
                  Invoice Without VAT {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}<span style={{ color: 'red' }}> *</span>
                </label>
                <NumericFormat
                  id="invoice_without_vat"
                  name="invoice_without_vat"
                  value={formData.invoice_without_vat}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  allowLeadingZeros={false}
                  inputMode="decimal"
                  className={`input ${fieldErrors.invoice_without_vat ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.invoice_without_vat ? "#d32f2f" : "" }}
                  placeholder="0,00"
                  autoComplete="off"
                  required
                  onValueChange={({ floatValue }) => {
                    setFormData((prev) => ({
                      ...prev,
                      invoice_without_vat: floatValue ?? "",
                    }));
                    if (errors.invoice_without_vat) setErrors((prev) => ({ ...prev, invoice_without_vat: null }));
                    if (fieldErrors.invoice_without_vat) setFieldErrors((prev) => ({ ...prev, invoice_without_vat: "" }));
                  }}
                />
              </div>
            </div>
            <RowErrorMessage errors={{
              bill_maturity: fieldErrors.bill_maturity,
              invoice_without_vat: fieldErrors.invoice_without_vat
            }} />

            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">
                  Currency <span style={{ color: 'red' }}>*</span>
                </label>
                <Select
                  options={currencies}
                  classNamePrefix="react-select"
                  value={selectedCurrency}
                  onChange={handleCurrencyChange}
                  placeholder="Select currency"
                  components={animatedComponents}
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    ...selectPortalStyles,
                    control: (provided) => ({
                      ...provided,
                      borderRadius: "8px",
                      borderColor: fieldErrors.currency ? "#d32f2f" : provided.borderColor,
                      "&:hover": {
                        borderColor: fieldErrors.currency ? "#d32f2f" : provided.borderColor,
                      },
                    }),
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="label">
                  Exchange Rate {!isSystemCurrency && <span style={{ color: 'red' }}>*</span>}
                </label>
                <Select
                  options={exchangeOptions}
                  classNamePrefix="react-select"
                  value={selectedExchangeRate}
                  onChange={handleExchangeRateChange}
                  placeholder={
                    !formData.currency
                      ? "Select currency first"
                      : isSystemCurrency
                      ? "Not needed"
                      : exchangeOptions.length
                      ? "Latest rate selected automatically"
                      : "No rates"
                  }
                  components={animatedComponents}
                  isClearable
                  isDisabled={!formData.currency || isSystemCurrency}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  formatOptionLabel={(option, { context }) =>
                    context === "value"
                      ? option.label
                      : `${option.label}${
                          option.dateLabel
                            ? ` (${formatDateLabel(option.dateLabel)})`
                            : ""
                        }`
                  }
                  styles={{
                    ...selectPortalStyles,
                    control: (provided) => ({
                      ...provided,
                      borderRadius: "8px",
                      borderColor: fieldErrors.exchange_rate ? "#d32f2f" : provided.borderColor,
                      "&:hover": {
                        borderColor: fieldErrors.exchange_rate ? "#d32f2f" : provided.borderColor,
                      },
                    }),
                  }}
                />
              </div>
            </div>
            <RowErrorMessage errors={{
              currency: fieldErrors.currency,
              exchange_rate: fieldErrors.exchange_rate
            }} />

            {/* Supplier VAT and Total Invoice with VAT - ROW 3 */}
            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="supplier_vat" className="label">
                  Supplier VAT (%) <span style={{ color: 'red' }}>*</span>
                </label>
                <NumericFormat
                  id="supplier_vat"
                  name="supplier_vat"
                  value={formData.supplier_vat}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  allowLeadingZeros={false}
                  inputMode="decimal"
                  className={`input ${fieldErrors.supplier_vat ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.supplier_vat ? "#d32f2f" : "" }}
                  placeholder="0,00"
                  required
                  onValueChange={({ floatValue }) => {
                    setFormData((prev) => ({
                      ...prev,
                      supplier_vat: floatValue ?? "",
                    }));
                    if (errors.supplier_vat) setErrors((prev) => ({ ...prev, supplier_vat: null }));
                    if (fieldErrors.supplier_vat) setFieldErrors((prev) => ({ ...prev, supplier_vat: "" }));
                  }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="total_invoice_with_vat" className="label">
                  Total Invoice with VAT {selectedCurrency?.label ? `(${selectedCurrency.label})` : ""}<span style={{ color: 'red' }}> *</span>
                </label>
                <NumericFormat
                  id="total_invoice_with_vat"
                  name="total_invoice_with_vat"
                  value={formData.total_invoice_with_vat}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  inputMode="decimal"
                  className={`input ${fieldErrors.total_invoice_with_vat ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.total_invoice_with_vat ? "#d32f2f" : "" }}
                  placeholder="0,00"
                  readOnly
                  disabled
                />
              </div>
            </div>
            <RowErrorMessage errors={{
              supplier_vat: fieldErrors.supplier_vat,
              total_invoice_with_vat: fieldErrors.total_invoice_with_vat
            }} />

            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="invoice_without_vat_system" className="label">
                  Total System without VAT
                  {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
                </label>
                <NumericFormat
                  id="invoice_without_vat_system"
                  name="invoice_without_vat_system"
                  value={formData.invoice_without_vat_system}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  customInput="input"
                  disabled
                  className="input"
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="total_invoice_with_vat_system" className="label">
                  Total System with VAT
                  {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
                </label>
                <NumericFormat
                  id="total_invoice_with_vat_system"
                  name="total_invoice_with_vat_system"
                  value={formData.total_invoice_with_vat_system}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  customInput="input"
                  disabled
                  className="input"
                />
              </div>
            </div>

            {/* Medium Alert and Critical Alert - ROW 4 */}
            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="medium_alert" className="label">
                  Medium Alert (default 5 days) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="medium_alert"
                  type="number"
                  name="medium_alert"
                  value={formData.medium_alert}
                  onChange={handleChange}
                  className={`input ${fieldErrors.medium_alert ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.medium_alert ? "#d32f2f" : "" }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="critical_alert" className="label">
                  Critical Alert (default 1 day) <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="critical_alert"
                  type="number"
                  name="critical_alert"
                  value={formData.critical_alert}
                  onChange={handleChange}
                  className={`input ${fieldErrors.critical_alert ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.critical_alert ? "#d32f2f" : "" }}
                />
              </div>
            </div>
            {/* ADAUGĂ ROW ERROR MESSAGE PENTRU ALERTS */}
            <RowErrorMessage errors={{
              medium_alert: fieldErrors.medium_alert,
              critical_alert: fieldErrors.critical_alert
            }} />

            {/* Month and Year - ROW 5 */}
            <div className="form-group-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="month" className="label">
                  Month <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="month"
                  type="text"
                  name="month"
                  value={monthChoices.find(m => m.value === formData.month)?.label || ''}
                  onChange={handleChange}
                  className={`input ${fieldErrors.month ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.month ? "#d32f2f" : "" }}
                  disabled
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="year" className="label">
                  Year <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id="year"
                  type="text"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  className={`input ${fieldErrors.year ? "input-error" : ""}`}
                  style={{ borderColor: fieldErrors.year ? "#d32f2f" : "" }}
                  disabled
                />
              </div>
            </div>
            <RowErrorMessage errors={{
              month: fieldErrors.month,
              year: fieldErrors.year
            }} />

            {/* Remarks */}
            <div className="form-group">
              <label htmlFor="remarks" className="label">Remarks</label>
              <textarea
                id="remarks"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                className="textarea"
              />
            </div>

            {/* Status */}
            <div className="form-group">
              <Typography
                sx={{
                  fontWeight: "bold",
                  color: "#2e2e2e",
                  fontSize: "14px",
                }}
              >
                Status: Unpaid
              </Typography>
            </div>

            {/* Buttons */}
            <div className="form-group-row">
              <div className="button-group-inline">
                <button type="button" className="button discard" onClick={() => navigate(-1)}>
                  Discard
                </button>
                <button
                  type="submit"
                  className={`button save ${isSaving ? "disabled" : ""}`}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddExternalResourceInvoice;